import type { ReactNode } from 'react';
import { twJoin } from 'tailwind-merge';

import { twm } from '../../../core/themes/tw-merge';
import { Spinner } from '../Spinner';

export type ButtonViewProps = {
  className?: string;
  children?: ReactNode;
  isDisabled?: boolean;
  isLoading?: boolean;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
  contentStretch?: boolean;
  variant?: 'primary' | 'secondary' | 'tertiary' | 'error';
  size?: 'small' | 'medium';
};

export const ButtonView = ({
  children,
  isLoading,
  leftElement,
  rightElement,
  className,
  isDisabled,
  variant,
  contentStretch,
  size = 'medium',
}: ButtonViewProps) => {
  return (
    <div
      className={twm(
        'relative rounded-md cursor-pointer flex items-center gap-2 text-center justify-center w-full',
        size === 'small' && 'h-10 px-lg',
        size === 'medium' && 'h-12 px-xl',
        size === 'small' && variant === 'tertiary' && 'h-8 px-sm',
        size === 'medium' && variant === 'tertiary' && 'h-9 px-md',
        isDisabled && !isLoading && 'cursor-not-allowed',
        variant === 'primary' && 'bg-surface-accent text-text-on-accent hover:bg-surface-accent-hover',
        variant === 'secondary' &&
          'bg-surface-secondary text-text-secondary hover:bg-surface-secondary-hover hover:text-text-secondary-hover',
        variant === 'tertiary' && 'text-text-secondary hover:bg-surface-primary-hover hover:text-text-secondary-hover',
        variant === 'error' && 'bg-surface-error-primary text-text-on-accent hover:bg-surface-error-primary-hover',
        isDisabled &&
          !isLoading &&
          'bg-surface-disabled hover:bg-surface-disabled text-text-disabled hover:text-text-disabled',
        className,
      )}
    >
      {isLoading ? null : leftElement}
      <div className={twJoin(isLoading && 'opacity-0', contentStretch && 'flex-1')}>
        {typeof children === 'string' ? (
          <p className="text-inherit text-body-md leading-lh-body-md font-medium">{children}</p>
        ) : (
          children
        )}
      </div>
      {isLoading ? null : rightElement}
      {isLoading ? (
        <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center">
          <Spinner />
        </div>
      ) : null}
    </div>
  );
};
